import React, { useEffect, useState } from "react";

import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { ButtonVariant } from "Constant/button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import { useSelector } from "react-redux";

import useHostFooterAction from "components/organisms/CreationModal/hooks/useHostFooterAction";

import "../../css/createSourceModal.css";
import {
    adjustMapping,
    sendAdjustData,
} from "../../helpers/Sources/adjust/adjust";
import { usageSourceCode } from "../../helpers/Sources/createSource";
import StandartButton from "../atoms/StandartButton";
import SwalAlertComp from "../SwalAlert";
import MmpPlatformSelector from "./MmpPlatformSelector";

// Default mapping for Adjust - empty mappings
const ADJUST_DEFAULT_MAPPING = { mappings: {} };

const EMPTY_PLATFORM_INPUT = { name: "", app_id: "" };

const AdjustSource = (props) => {
    const { useHostFooter = false, registerFooterAction } = props;
    const [selectedPlatforms, setSelectedPlatforms] = useState([]);
    const [platformInputs, setPlatformInputs] = useState({
        android: { ...EMPTY_PLATFORM_INPUT },
        ios: { ...EMPTY_PLATFORM_INPUT },
        windows: { ...EMPTY_PLATFORM_INPUT },
    });
    const [isSaveLoading, setIsSaveLoading] = useState(false);
    const [platformError, setPlatformError] = useState("");
    const [fieldErrors, setFieldErrors] = useState({
        android: { name: "", app_id: "" },
        ios: { name: "", app_id: "" },
        windows: { name: "", app_id: "" },
    });

    const [usage, setUsage] = useState();

    const language = useSelector((state) => state.languagesState.languages);
    const cannotBeBlank =
        language?.tracker_wizard?.cannot_be_blank ?? "Cannot be blank!";

    const setLoadingState = (loading) => {
        setIsSaveLoading(loading);
        props?.setLoading?.(loading);
    };

    const handleCloseModal = () => {
        props?.handleClose();
    };

    const handlePlatformInputChange = (platform, field, value) => {
        setPlatformInputs((prev) => ({
            ...prev,
            [platform]: { ...prev[platform], [field]: value },
        }));
        setFieldErrors((prev) => ({
            ...prev,
            [platform]: {
                ...prev[platform],
                [field]: value ? "" : cannotBeBlank,
            },
        }));
    };

    const appIdRender = (app_id, platform, prefix, length) => {
        if (platform === "ios") {
            if (app_id.slice(0, length) !== prefix) {
                return prefix + app_id;
            } else {
                return app_id;
            }
        } else {
            return app_id;
        }
    };

    const validate = () => {
        if (selectedPlatforms.length === 0) {
            setPlatformError(cannotBeBlank);
            return false;
        }
        setPlatformError("");

        let valid = true;
        const newFieldErrors = {
            android: { name: "", app_id: "" },
            ios: { name: "", app_id: "" },
            windows: { name: "", app_id: "" },
        };

        selectedPlatforms.forEach((platform) => {
            const input = platformInputs[platform];
            if (!input.name) {
                newFieldErrors[platform].name = cannotBeBlank;
                valid = false;
            }
            if (!input.app_id) {
                newFieldErrors[platform].app_id = cannotBeBlank;
                valid = false;
            }
        });

        setFieldErrors(newFieldErrors);
        return valid;
    };

    const fetchAdjustData = async () => {
        if (!validate()) return;

        setLoadingState(true);

        const createdSourceIds = [];
        const errors = [];

        try {
            for (const platform of selectedPlatforms) {
                const input = platformInputs[platform];
                const data = {
                    name: input.name,
                    app_id: appIdRender(input.app_id, platform, "id", 2),
                    platform,
                    usage_types: usage,
                };

                const response = await sendAdjustData(data);
                if (response.status) {
                    const sourceId = response?.data?.source?.id;
                    const sourceTypeId = response?.data?.source?.source_type_id;

                    if (props.selectFileSourceId) {
                        props.selectFileSourceId(sourceId, sourceTypeId);
                    }

                    const mappingResponse = await adjustMapping(
                        ADJUST_DEFAULT_MAPPING,
                        sourceId,
                    );

                    if (mappingResponse.status) {
                        createdSourceIds.push(sourceId);
                        if (props?.setIsMappingSuccessfull) {
                            props?.setIsMappingSuccessfull(true);
                        }
                    } else {
                        errors.push(
                            `${platform}: ${mappingResponse?.message ?? "Failed to save mapping"}`,
                        );
                    }
                } else {
                    errors.push(
                        `${platform}: ${response?.message ?? "Failed to create source"}`,
                    );
                }
            }

            if (errors.length > 0) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: errors.join("\n"),
                    icon: "error",
                });
            }

            if (createdSourceIds.length > 0) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.success ?? "Success",
                    message: "Source created successfully",
                    icon: "success",
                    timer: 3000,
                });

                if (props.getAllSourcesReduxProps) {
                    props.getAllSourcesReduxProps();
                }
                if (props.setNextCheck) {
                    props.setNextCheck(true);
                }
                props?.handleClose(
                    createdSourceIds.length === 1
                        ? createdSourceIds[0]
                        : createdSourceIds,
                );
                if (props.handleGetSources) {
                    props.handleGetSources();
                }
                if (props.getTracker) {
                    await props.addToTracker(
                        createdSourceIds.length === 1
                            ? createdSourceIds[0]
                            : createdSourceIds,
                    );
                }
            }
        } finally {
            setLoadingState(false);
        }
    };

    useEffect(() => {
        let fetch = async () => {
            let response = await usageSourceCode("adjust");
            if (response?.status) {
                const tempNameOptions = response?.data?.map((item) => ({
                    value: item?.code,
                    label: item?.name,
                    id: item?.id,
                }));
                const defaultUsage = tempNameOptions?.filter(
                    (option) => option?.label === "App",
                )[0]?.id;
                setUsage([defaultUsage]);
            }
        };
        fetch();
    }, []);

    const platformLabels = {
        android: "Android",
        ios: "iOS",
        windows: "Windows",
    };

    useHostFooterAction(registerFooterAction, "form", {
        label: language?.buttons?.create_source ?? "Create source",
        onSubmit: fetchAdjustData,
        isLoading: isSaveLoading,
    });

    const saveButton = (
        <StandartButton
            variant="contained"
            color="primary"
            onClick={fetchAdjustData}
            isLoading={isSaveLoading}
            disabled={isSaveLoading}
            wrapperSx={{ marginLeft: "auto", width: "fit-content" }}
            data-cy="create-source-mmp-submit"
        >
            {language?.buttons?.create_source ?? "Create Source"}
        </StandartButton>
    );

    return (
        <div className="adjust-create-modal">
            <Form.Group
                className="mb-3"
                controlId={"exampleForm.ControlInput1"}
            >
                <MmpPlatformSelector
                    selectedPlatforms={selectedPlatforms}
                    onChange={(platforms) => {
                        setSelectedPlatforms(platforms);
                        if (platforms.length > 0) setPlatformError("");
                    }}
                    disabled={isSaveLoading}
                    error={platformError}
                    label={language?.source_modal?.platform ?? "Platform"}
                />

                {selectedPlatforms.map((platform) => (
                    <div key={platform} style={{ marginBottom: "16px" }}>
                        {selectedPlatforms.length > 1 && (
                            <Typography
                                variant="subtitle2"
                                style={{
                                    fontWeight: 600,
                                    marginBottom: "8px",
                                    textTransform: "capitalize",
                                }}
                            >
                                {platformLabels[platform]}
                            </Typography>
                        )}

                        <FormControl className="mb-3" style={{ width: "100%" }}>
                            <TextField
                                label={language?.source_modal?.name ?? "Name"}
                                variant="outlined"
                                onChange={(e) =>
                                    handlePlatformInputChange(
                                        platform,
                                        "name",
                                        e.target.value,
                                    )
                                }
                                placeholder={
                                    language?.source_modal?.please_write_name ??
                                    "Please Write Name"
                                }
                                value={platformInputs[platform].name}
                                required
                                disabled={isSaveLoading}
                                inputProps={{ "data-cy": "mmp-app-name" }}
                            />
                            {fieldErrors[platform]?.name && (
                                <div className="fileUploadStyle">
                                    {fieldErrors[platform].name}
                                </div>
                            )}
                        </FormControl>

                        <FormControl className="mb-3" style={{ width: "100%" }}>
                            <TextField
                                label={
                                    language?.source_modal?.app_id ?? "APP ID"
                                }
                                variant="outlined"
                                onChange={(e) =>
                                    handlePlatformInputChange(
                                        platform,
                                        "app_id",
                                        e.target.value,
                                    )
                                }
                                placeholder={
                                    language?.source_modal
                                        ?.please_write_app_id ??
                                    "Please Write APP ID"
                                }
                                value={platformInputs[platform].app_id}
                                required
                                disabled={isSaveLoading}
                                inputProps={{ "data-cy": "mmp-app-id" }}
                            />
                            {fieldErrors[platform]?.app_id && (
                                <div className="fileUploadStyle">
                                    {fieldErrors[platform].app_id}
                                </div>
                            )}
                        </FormControl>
                    </div>
                ))}
            </Form.Group>

            {!useHostFooter &&
                (props?.btnCheck ? (
                    saveButton
                ) : (
                    <Modal.Footer className="source-modal-footer">
                        <StandartButton
                            variant={ButtonVariant.BLACK_OUTLINED}
                            onClick={handleCloseModal}
                            disabled={isSaveLoading}
                        >
                            {language?.buttons?.close ?? "Close"}
                        </StandartButton>
                        {saveButton}
                    </Modal.Footer>
                ))}
        </div>
    );
};

export default AdjustSource;
